import { addDays, format, parseISO, startOfISOWeek } from 'date-fns'

import { dateFnsLocale } from '@/shared/lib/dateFnsLocale'

import type { PlayIntentWindow, PlayIntentWindowKind } from '../types/playIntent.types'

/**
 * Lógica pura de las franjas de "Ganas de jugar" (el servidor las calcula en
 * hora de Madrid; aquí solo se nombran y se derivan fechas para el asistente).
 *
 * Todas las claves i18n son LITERALES (CLAUDE.md § claves i18n literales).
 */

/** Nombre de la franja relativo a hoy. */
export const WINDOW_NAME_KEYS = {
  WEEKDAYS: {
    current: 'playIntents.window.thisWeekdays',
    next: 'playIntents.window.nextWeekdays',
  },
  WEEKEND: {
    current: 'playIntents.window.thisWeekend',
    next: 'playIntents.window.nextWeekend',
  },
} as const

/** Misma franja en minúscula y dentro de frase ("… a Catan este finde en Madrid"). */
export const WINDOW_INLINE_KEYS = {
  WEEKDAYS: {
    current: 'playIntents.windowInline.thisWeekdays',
    next: 'playIntents.windowInline.nextWeekdays',
  },
  WEEKEND: {
    current: 'playIntents.windowInline.thisWeekend',
    next: 'playIntents.windowInline.nextWeekend',
  },
} as const

/** `YYYY-MM-DD` → Date a las 00:00 locales (parseISO trata la fecha sola como local). */
function day(date: string): Date {
  return parseISO(date)
}

/** ¿`date` (YYYY-MM-DD) cae en la semana ISO (lunes-domingo) de `now`? */
export function isInCurrentIsoWeek(date: string, now: Date = new Date()): boolean {
  return startOfISOWeek(day(date)).getTime() === startOfISOWeek(now).getTime()
}

/**
 * Clave del nombre de una franja por su inicio: semana ISO actual → "este finde"
 * / "esta semana"; si no → "el finde que viene" / "la semana que viene".
 * Para grupos y notificaciones, que no traen `current`.
 */
export function relativeWindowNameKey(
  kind: PlayIntentWindowKind,
  windowStart: string,
  now: Date = new Date(),
): string {
  const rel = isInCurrentIsoWeek(windowStart, now) ? 'current' : 'next'
  return WINDOW_NAME_KEYS[kind][rel]
}

/** Igual que {@link relativeWindowNameKey} pero en su forma dentro de frase. */
export function relativeWindowInlineKey(
  kind: PlayIntentWindowKind,
  windowStart: string,
  now: Date = new Date(),
): string {
  const rel = isInCurrentIsoWeek(windowStart, now) ? 'current' : 'next'
  return WINDOW_INLINE_KEYS[kind][rel]
}

/** Clave del nombre de una franja de `/windows` (trae `current` del servidor). */
export function windowNameKey(window: PlayIntentWindow): string {
  return WINDOW_NAME_KEYS[window.kind][window.current ? 'current' : 'next']
}

/**
 * Rango legible: "lun 29 sep – vie 3 oct"; si empieza y acaba en el mismo mes,
 * el mes solo al final ("sáb 4 – dom 5 oct").
 */
export function formatWindowRange(start: string, end: string, language: string): string {
  const locale = dateFnsLocale(language)
  const from = day(start)
  const to = day(end)
  const sameMonth = from.getMonth() === to.getMonth()
  const fromLabel = format(from, sameMonth ? 'EEE d' : 'EEE d MMM', { locale })
  const toLabel = format(to, 'EEE d MMM', { locale })
  return `${fromLabel} – ${toLabel}`
}

/** Último día de una franja a partir de su inicio (lunes → viernes, sábado → domingo). */
export function windowEndFor(kind: PlayIntentWindowKind, windowStart: string): string {
  return format(addDays(day(windowStart), kind === 'WEEKDAYS' ? 4 : 1), 'yyyy-MM-dd')
}

/** Hora por defecto que el asistente propone para una partida nacida de unas ganas. */
export const SUGGESTED_HOUR = 18

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Fecha+hora que el asistente propone para convertir unas ganas en partida, en
 * el formato del picker (`YYYY-MM-DDTHH:mm`, hora local):
 *
 * - El primer día de la franja, desde mañana, cuyas 18:00 queden a ≥ 24 h
 *   (respeta el consejo de antelación del asistente).
 * - Si ninguno llega a 24 h, el primer día desde mañana (el consejo se ve, no
 *   bloquea).
 * - Si la franja ya no tiene días desde mañana, su último día.
 */
export function suggestedScheduledAt(
  windowStart: string,
  windowEnd: string,
  now: Date = new Date(),
): string {
  const tomorrow = addDays(new Date(now.getFullYear(), now.getMonth(), now.getDate()), 1)
  const end = day(windowEnd)
  const candidates: Date[] = []
  for (let d = day(windowStart); d.getTime() <= end.getTime(); d = addDays(d, 1)) {
    if (d.getTime() >= tomorrow.getTime()) {
      candidates.push(new Date(d.getFullYear(), d.getMonth(), d.getDate(), SUGGESTED_HOUR))
    }
  }
  const pick =
    candidates.find((c) => c.getTime() - now.getTime() >= DAY_MS) ??
    candidates[0] ??
    new Date(end.getFullYear(), end.getMonth(), end.getDate(), SUGGESTED_HOUR)
  return format(pick, "yyyy-MM-dd'T'HH:mm")
}

/**
 * Provincia de una ciudad. Los códigos de ciudad son los de municipio del INE
 * (6 dígitos) y sus 2 primeros son el código de provincia (`280796` → `28`),
 * igual que en `cities.province_code` del baseline. El asistente de crear
 * partida necesita la provincia para su cascada provincia → ciudad → zona.
 */
export function provinceCodeOfCity(cityCode: string): string {
  return cityCode.slice(0, 2)
}
